import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { TFunction } from "i18next";
import React from "react";
import { AutoGenerateShiftSetting } from "web-apps/company/components/companySettingsV2/companyStampClock/autoGenerateShiftSetting.component";
import { LocalStampClockSetting } from "web-apps/company/components/companySettingsV2/companyStampClock/localStampClockSetting.component";
import { AutoAcceptAllGigsSetting } from "./autoAcceptAllGigsSetting.component";
import { BreakSetting } from "./breakSetting.component";
import { CompanyBankAccountSetting } from "./companyBankAccountSetting.component";
import { CompanyTimeReportIncludeFlexTimes } from "./companyTimeReportIncludeFlexTimes.component";
import { CompanyMonthlySalaryIgnoreAbsenceSetting } from "./companyMonthlySalaryIgnoreAbsenceSetting.component";
import { CompanyTimeZoneSetting } from "./companyTimeZoneSetting.component";
import { DeductionSetting } from "./deductionSetting.component";
import { PercentageInput } from "./percentageInput.component";
import { TimeSettings } from "./timeSettings.component";
import { AllowEditTimeStampClockSetting } from "./allowEditTimeStampClockSetting.component";
import { TimeReportDeviationThresholdSetting } from "./timeReportDeviationThresholdSetting.component";
import { TimeReportStampThresholdSettings } from "./timeReportStampThresholdSettings.component";
import {
  ModuleSettingDescription,
  ModuleSettingRow,
  ModuleSettingsCard,
  ModuleSettingText,
  ModuleSettingTitle,
  ModuleSwitch,
  ModuleSwitchThumb,
  SystemSettingsActions,
  SystemSettingsCategoryContent,
} from "./companySystemSettings.styled";
import {
  CompanySystemSettingsFormData,
  ModuleSettingItem,
  ModuleSettingKey,
  SystemSettingsCategory,
} from "./companySystemSettings.types";
import { stringNormalize } from "./companySystemSettings.logic";

type Props = {
  activeCategory: SystemSettingsCategory;
  formData: CompanySystemSettingsFormData;
  setFormData: React.Dispatch<
    React.SetStateAction<CompanySystemSettingsFormData>
  >;
  moduleSettings: ModuleSettingItem[];
  setModuleToggle: (key: ModuleSettingKey) => (value: boolean) => void;
  activeCategoryHasSettings: boolean;
  activeCategoryHasChanges: boolean;
  canSave: boolean;
  isSaving: boolean;
  handleUpdate: () => void;
  t: TFunction;
};

export const CompanySystemSettingsContent: React.FC<Props> = ({
  activeCategory,
  formData,
  setFormData,
  moduleSettings,
  setModuleToggle,
  activeCategoryHasSettings,
  activeCategoryHasChanges,
  canSave,
  isSaving,
  handleUpdate,
  t,
}) => {
  const renderModuleSettings = (): React.ReactElement | null => {
    if (moduleSettings.length === 0) return null;

    const shouldRenderSaveButtonInModuleCard = !activeCategoryHasSettings;

    return (
      <ModuleSettingsCard>
        {moduleSettings.map((setting) => {
          const active = formData[setting.key];

          return (
            <ModuleSettingRow key={setting.key}>
              <ModuleSettingText>
                <ModuleSettingTitle>{setting.title}</ModuleSettingTitle>
                <ModuleSettingDescription>
                  {setting.description}
                </ModuleSettingDescription>
              </ModuleSettingText>
              <ModuleSwitch
                type="button"
                $active={active}
                aria-pressed={active}
                aria-label={setting.title}
                onClick={() => setModuleToggle(setting.key)(!active)}
              >
                <ModuleSwitchThumb $active={active} />
              </ModuleSwitch>
            </ModuleSettingRow>
          );
        })}
        {shouldRenderSaveButtonInModuleCard && (
          <SystemSettingsActions>
            <PrimaryButton
              label={t("General.Save")}
              size="large"
              disabled={!activeCategoryHasChanges || !canSave}
              isLoading={isSaving}
              onClick={handleUpdate}
            />
          </SystemSettingsActions>
        )}
      </ModuleSettingsCard>
    );
  };

  const renderGigsSettings = (): React.ReactElement => (
    <>
      <TimeSettings
        startTime={formData.companyGigShiftStartTime}
        endTime={formData.companyGigShiftEndTime}
        onChangeStartTime={(value) =>
          setFormData((previous) => ({
            ...previous,
            companyGigShiftStartTime: value,
          }))
        }
        onChangeEndTime={(value) =>
          setFormData((previous) => ({
            ...previous,
            companyGigShiftEndTime: value,
          }))
        }
      />
      <BreakSetting
        value={formData.break}
        onChange={(value) =>
          setFormData((previous) => ({ ...previous, break: value }))
        }
      />
      <DeductionSetting
        value={formData.deduction}
        onChange={(value) =>
          setFormData((previous) => ({ ...previous, deduction: value }))
        }
      />
      <AutoAcceptAllGigsSetting
        value={formData.autoAcceptAllGigs}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            autoAcceptAllGigs: value,
          }))
        }
      />
    </>
  );

  const renderTimeReportsSettings = (): React.ReactElement => (
    <>
      <TimeReportDeviationThresholdSetting
        value={formData.timeReportDeviationThreshold}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            timeReportDeviationThreshold: value,
          }))
        }
      />
      <CompanyTimeReportIncludeFlexTimes
        value={formData.companyTimeReportIncludeFlexTimes}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            companyTimeReportIncludeFlexTimes: value,
          }))
        }
      />
      <AllowEditTimeStampClockSetting
        value={formData.companyStampClockAllowEditTime}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            companyStampClockAllowEditTime: value,
          }))
        }
      />
    </>
  );

  const renderSalarySettings = (): React.ReactElement => (
    <>
      <CompanyMonthlySalaryIgnoreAbsenceSetting
        value={formData.companyMonthlySalaryIgnoreAbsence}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            companyMonthlySalaryIgnoreAbsence: stringNormalize(value),
          }))
        }
      />
      <PercentageInput
        value={formData.vacationPayPercentage}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            vacationPayPercentage: value,
          }))
        }
        min={12}
        max={25}
        title={t("System.CompanyVacationPayPercentage")}
        info={t("System.CompanyVacationPayPercentageInfo")}
      />
      <PercentageInput
        value={formData.companyMonthlySalaryVacationPayPercent}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            companyMonthlySalaryVacationPayPercent: value,
          }))
        }
        min={0.43}
        max={1}
        title={t("System.CompanyMonthlyVacationPayPercentage")}
        info={t("System.CompanyMonthlyVacationPayPercentageInfo")}
      />
    </>
  );

  const renderStampClockToleranceSettings = (): React.ReactElement => (
    <TimeReportStampThresholdSettings
      beforeStartValue={formData.timeReportStampBeforeStartThreshold}
      onChangeBeforeStart={(value) =>
        setFormData((previous) => ({
          ...previous,
          timeReportStampBeforeStartThreshold: value,
        }))
      }
      afterStartValue={formData.timeReportStampAfterStartThreshold}
      onChangeAfterStart={(value) =>
        setFormData((previous) => ({
          ...previous,
          timeReportStampAfterStartThreshold: value,
        }))
      }
      beforeEndValue={formData.timeReportStampBeforeEndThreshold}
      onChangeBeforeEnd={(value) =>
        setFormData((previous) => ({
          ...previous,
          timeReportStampBeforeEndThreshold: value,
        }))
      }
      afterEndValue={formData.timeReportStampAfterEndThreshold}
      onChangeAfterEnd={(value) =>
        setFormData((previous) => ({
          ...previous,
          timeReportStampAfterEndThreshold: value,
        }))
      }
    />
  );

  const renderStampClockSettings = (): React.ReactElement => (
    <>
      <LocalStampClockSetting
        value={formData.localStampClockSettingActive}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            localStampClockSettingActive: value,
          }))
        }
      />
      <AutoGenerateShiftSetting
        value={formData.autoGenerateShiftSettingActive}
        onChange={(value) =>
          setFormData((previous) => ({
            ...previous,
            autoGenerateShiftSettingActive: value,
          }))
        }
      />
    </>
  );

  const renderOtherSettings = (): React.ReactElement => (
    <CompanyTimeZoneSetting
      value={formData.companyTimeZone}
      onChange={(value) =>
        setFormData((previous) => ({ ...previous, companyTimeZone: value }))
      }
    />
  );

  const renderBankAccountSettings = (): React.ReactElement => (
    <CompanyBankAccountSetting
      agreementId={formData.companyBankAgreementId}
      accountValue={formData.companyBankAccountValue}
      accountType={formData.companyBankAccountType}
      onChangeAgreementId={(value) =>
        setFormData((previous) => ({
          ...previous,
          companyBankAgreementId: value,
        }))
      }
      onChangeAccountValue={(value) =>
        setFormData((previous) => ({
          ...previous,
          companyBankAccountValue: value,
        }))
      }
      onChangeAccountType={(value) =>
        setFormData((previous) => ({
          ...previous,
          companyBankAccountType: value,
        }))
      }
    />
  );

  const renderActiveCategory = (): React.ReactElement => {
    switch (activeCategory) {
      case "gigs":
        return renderGigsSettings();
      case "timeReports":
        return renderTimeReportsSettings();
      case "salary":
        return renderSalarySettings();
      case "stampClock":
        return renderStampClockSettings();
      case "tips":
      case "chat":
      case "ob":
        return <></>;
      case "other":
        return renderOtherSettings();
      case "bankAccount":
        return renderBankAccountSettings();
    }
  };

  return (
    <>
      {renderModuleSettings()}

      {activeCategoryHasSettings && activeCategory === "stampClock" && (
        <>
          <SystemSettingsCategoryContent>
            {renderStampClockToleranceSettings()}
          </SystemSettingsCategoryContent>
          <SystemSettingsCategoryContent>
            {renderStampClockSettings()}
            <SystemSettingsActions>
              <PrimaryButton
                label={t("General.Save")}
                size="large"
                disabled={!activeCategoryHasChanges || !canSave}
                isLoading={isSaving}
                onClick={handleUpdate}
              />
            </SystemSettingsActions>
          </SystemSettingsCategoryContent>
        </>
      )}

      {activeCategoryHasSettings && activeCategory !== "stampClock" && (
        <SystemSettingsCategoryContent>
          {renderActiveCategory()}
          <SystemSettingsActions>
            <PrimaryButton
              label={t("General.Save")}
              size="large"
              disabled={!activeCategoryHasChanges || !canSave}
              isLoading={isSaving}
              onClick={handleUpdate}
            />
          </SystemSettingsActions>
        </SystemSettingsCategoryContent>
      )}
    </>
  );
};
